import { useEffect, useRef, useState } from "react";
import { Building2 } from "lucide-react";
import { surveyApi } from "@/lib/apiClient";
import { T, hexA } from "./theme";
import type { Customer } from "@shared-domain/lib/customer/provider";
import type { GBizCompany } from "@shared-domain/lib/gbiz/types";

/**
 * 対象顧客名の入力補助 (PRODUCT_DEV-574 タップ入力)。左にビルアイコン。
 * 入力で顧客マスタ (既存) + gBizINFO (候補) を統合検索し候補を出す
 * (unizo 商談詳細の CompanyAutocomplete と同等・survey に閉じた移植)。
 */
export function CompanyAutocomplete({ value, onChange, onPickCustomer, onPickGbiz, placeholder, autoFocus }: {
  value: string;
  onChange: (name: string) => void;
  onPickCustomer?: (c: Customer) => void;
  onPickGbiz?: (g: GBizCompany) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [gbiz, setGbiz] = useState<GBizCompany[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const reqId = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const picked = useRef<string | null>(null); // 選択直後の再検索・再オープンを防ぐ

  useEffect(() => {
    const q = value.trim();
    if (q === picked.current) return; // 選択で確定した値は再検索しない
    if (q.length < 2) { setCustomers([]); setGbiz([]); setOpen(false); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const id = ++reqId.current;
      surveyApi.companySearch(q).then((r) => {
        if (id !== reqId.current) return; // 古い応答は破棄
        const names = new Set(r.customers.map((c) => c.name));
        setCustomers(r.customers.slice(0, 6));
        setGbiz(r.gbiz.filter((g) => !names.has(g.name)).slice(0, 6));
        setOpen(true);
      }).catch(() => { /* 検索失敗は無視 (手入力は継続可) */ });
    }, 300);
    return () => clearTimeout(timer.current);
  }, [value]);

  const pickCustomer = (c: Customer) => { picked.current = c.name; onChange(c.name); onPickCustomer?.(c); setOpen(false); inputRef.current?.blur(); };
  const pickGbiz = (g: GBizCompany) => { picked.current = g.name; onChange(g.name); onPickGbiz?.(g); setOpen(false); inputRef.current?.blur(); };

  const rowStyle: React.CSSProperties = { width: "100%", textAlign: "left", background: "#fff", border: 0, borderTop: "1px solid #F1F5F9", padding: "10px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 };
  const badge = (label: string, color: string, bg: string) => (
    <span style={{ fontSize: 10, fontWeight: 700, color, background: bg, borderRadius: 6, padding: "1px 6px", flex: "none" }}>{label}</span>
  );

  return (
    <div style={{ position: "relative" }}>
      <span style={{ position: "absolute", left: 13, top: 23, transform: "translateY(-50%)", pointerEvents: "none" }}><Building2 size={17} color={T.faint} /></span>
      <input
        ref={inputRef}
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => { picked.current = null; onChange(e.target.value); }}
        onFocus={() => { setFocus(true); if (customers.length || gbiz.length) setOpen(true); }}
        onBlur={() => { setFocus(false); setTimeout(() => setOpen(false), 150); }}
        placeholder={placeholder}
        style={{ width: "100%", height: 46, padding: "0 14px 0 40px", fontSize: 14.5, fontWeight: 600, color: T.ink, background: "#fff", border: `2px solid ${focus ? T.accent : "#E2E8F0"}`, borderRadius: 12, outline: "none", boxSizing: "border-box" }}
      />
      {open && (customers.length > 0 || gbiz.length > 0) && (
        <div style={{ position: "absolute", top: 50, left: 0, right: 0, zIndex: 40, background: "#fff", border: "1px solid #E8EDF3", borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,.14)", overflow: "hidden", maxHeight: 260, overflowY: "auto" }}>
          {customers.map((c) => (
            <button key={c.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pickCustomer(c)} style={rowStyle}>
              {badge("既存", T.accent, hexA(T.accent, 0.1))}
              <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
              {c.addr && <span style={{ fontSize: 11, color: T.faint, flex: "none", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.addr}</span>}
            </button>
          ))}
          {gbiz.map((g) => (
            <button key={g.corporateNumber || g.name} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pickGbiz(g)} style={rowStyle}>
              {badge("候補", T.sub, "#F1F5F9")}
              <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.name}</span>
              {g.location && <span style={{ fontSize: 11, color: T.faint, flex: "none", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.location}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
